import React from "react";
import { toast } from "sonner";
import { AtSign, Facebook, Instagram, Copy, Send } from "lucide-react";

// One-tap posting using a fact's saved Social Caption.
// Threads pre-fills the composer; Facebook opens its share dialog; Instagram has no
// web post API, so we copy the caption and open the app ready to paste.
const INSTAGRAM_HANDLE = "curiovaultco";

const copyCaption = async (caption) => {
  try {
    await navigator.clipboard.writeText(caption);
    return true;
  } catch (e) {
    console.error("Clipboard copy failed:", e);
    return false;
  }
};

export const SocialPost = ({ caption, url, variant = "panel" }) => {
  const text = caption || "";
  const shareText = `${text} ${url}`.trim();

  const open = (href) => window.open(href, "_blank", "noopener,noreferrer");

  const postThreads = async () => {
    await copyCaption(text);
    open(`https://www.threads.net/intent/post?text=${encodeURIComponent(shareText)}`);
    toast.success("Threads opened with your caption pre-filled.");
  };

  const postFacebook = async () => {
    await copyCaption(text);
    open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`);
    toast.success("Facebook share opened. Caption copied to paste if needed.");
  };

  const postInstagram = async () => {
    const ok = await copyCaption(text);
    open(`https://www.instagram.com/${INSTAGRAM_HANDLE}/`);
    toast.success(ok ? "Caption copied — paste it into your Instagram post." : "Instagram opened.");
  };

  const justCopy = async () => {
    const ok = await copyCaption(text);
    toast[ok ? "success" : "error"](ok ? "Caption copied to clipboard." : "Could not copy caption.");
  };

  if (variant === "inline") {
    const btn = "w-8 h-8 flex items-center justify-center rounded-full border border-[#D8CEB9] text-[#4A4036] hover:bg-[#9E4535] hover:text-[#FAF7F0] hover:border-[#9E4535] transition-colors";
    return (
      <div className="flex items-center gap-1.5" data-testid="social-post-inline">
        <button onClick={postThreads} className={btn} title="Post to Threads" aria-label="Post to Threads" data-testid="post-threads"><AtSign className="w-3.5 h-3.5" /></button>
        <button onClick={postInstagram} className={btn} title="Post to Instagram" aria-label="Post to Instagram" data-testid="post-instagram"><Instagram className="w-3.5 h-3.5" /></button>
        <button onClick={postFacebook} className={btn} title="Post to Facebook" aria-label="Post to Facebook" data-testid="post-facebook"><Facebook className="w-3.5 h-3.5" /></button>
        <button onClick={justCopy} className={btn} title="Copy caption" aria-label="Copy caption" data-testid="post-copy"><Copy className="w-3.5 h-3.5" /></button>
      </div>
    );
  }

  return (
    <div className="bg-[#FDF8ED] border border-[#E4D3A8] rounded-sm p-5" data-testid="social-post-panel">
      <div className="flex items-center gap-2 mb-2">
        <Send className="w-4 h-4 text-[#B08A42]" />
        <h3 className="font-serif-display text-lg font-semibold text-[#1A1615]">Post to Your Socials</h3>
      </div>
      <p className="font-body text-sm text-[#4A4036] italic mb-4">“{text}”</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={postThreads} data-testid="post-threads" className="inline-flex items-center gap-2 font-ui text-xs font-semibold px-4 py-2.5 bg-[#1A1615] text-[#FAF7F0] rounded-sm hover:bg-[#332b27] transition-colors">
          <AtSign className="w-4 h-4" /> Threads
        </button>
        <button onClick={postInstagram} data-testid="post-instagram" className="inline-flex items-center gap-2 font-ui text-xs font-semibold px-4 py-2.5 bg-[#B0357E] text-[#FAF7F0] rounded-sm hover:bg-[#96296A] transition-colors">
          <Instagram className="w-4 h-4" /> Instagram
        </button>
        <button onClick={postFacebook} data-testid="post-facebook" className="inline-flex items-center gap-2 font-ui text-xs font-semibold px-4 py-2.5 bg-[#3B5998] text-[#FAF7F0] rounded-sm hover:bg-[#314a80] transition-colors">
          <Facebook className="w-4 h-4" /> Facebook
        </button>
        <button onClick={justCopy} data-testid="post-copy" className="inline-flex items-center gap-2 font-ui text-xs font-semibold px-4 py-2.5 bg-white text-[#4A4036] border border-[#D8CEB9] rounded-sm hover:border-[#9E4535] transition-colors">
          <Copy className="w-4 h-4" /> Copy caption
        </button>
      </div>
      <p className="font-mono-cv text-[10px] text-[#B8AC9C] mt-3">
        Threads pre-fills the caption. Instagram has no web posting API, so the caption is copied for you to paste.
      </p>
    </div>
  );
};
